import {
  Appointment,
  BookingData,
  ApiResponse,
  ServiceType,
  ProgressStep,
  PaymentMethod,
  WeekAppointmentsMeta,
} from "../types";
import { apiClient } from "./apiClient";

export const appointmentsApi = {
  // Book an appointment
  async bookAppointment(
    bookingData: BookingData
  ): Promise<ApiResponse<Appointment>> {
    try {
      // Transform BookingData to the required API format
      const requestBody: any = {
        professionalId: bookingData.professionalId,
        serviceId: bookingData.serviceId,
        appointmentDate: bookingData.date,
        phoneNumber: bookingData.phoneNumber,
        clientId: bookingData.clientId,
        clientName: bookingData.clientName, // For new client creation
        subServiceIds: bookingData.subServiceIds || [],
      };

      if (bookingData.price != null && bookingData.price !== "") {
        requestBody.price = bookingData.price;
      }

      if (bookingData.clientCarId != null) {
        requestBody.clientCarId = bookingData.clientCarId;
      }

      // Only include startTime if service type is TIME
      if (
        bookingData.serviceType === ServiceType.TIME &&
        bookingData.startTime
      ) {
        requestBody.startTime = bookingData.startTime;
      }

      const data = await apiClient.post<ApiResponse<Appointment>>(
        "/appointments",
        requestBody
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error: any) {
      console.error("Error booking appointment:", error);
      return {
        success: false,
        error: error.message || "Failed to book appointment",
      };
    }
  },

  // Get appointments by professional and date
  async getAppointmentsByProfessionalAndDate(
    professionalId: number,
    date: string
  ): Promise<ApiResponse<Appointment[]>> {
    try {
      // Ensure date is in yyyy-mm-dd format
      const formattedDate = date.includes("T") ? date.split("T")[0] : date;

      const data = await apiClient.get<{ data: Appointment[] }>(
        `/appointments/professional/${professionalId}/date/${formattedDate}`
      );

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Get appointments by company and date
  async getAppointmentsByCompanyAndDate(
    date: string
  ): Promise<ApiResponse<Appointment[]>> {
    try {
      // Ensure date is in yyyy-mm-dd format
      const formattedDate = date.includes("T") ? date.split("T")[0] : date;

      const data = await apiClient.get<{ data: Appointment[] }>(
        `/appointments/date/${formattedDate}`
      );

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Get appointment by ID
  async getAppointmentById(
    appointmentId: number
  ): Promise<ApiResponse<Appointment>> {
    try {
      const response = await apiClient.get<
        { data: Appointment } | Appointment
      >(`/appointments/${appointmentId}`);

      const appointment =
        response &&
        typeof response === "object" &&
        "data" in response &&
        response.data
          ? response.data
          : (response as Appointment);

      return {
        success: true,
        data: {
          ...appointment,
          progressSteps: appointment.progressSteps ?? [],
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Get appointments by client ID
  async getAppointmentsByClientId(
    clientId: number
  ): Promise<ApiResponse<Appointment[]>> {
    try {
      const data = await apiClient.get<{ data: Appointment[] }>(
        `/appointments?clientId=${clientId}`
      );

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Update appointment
  async updateAppointment(
    appointmentId: number,
    updateData: Partial<Appointment>
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.put<{ data: Appointment }>(
        `/appointments/${appointmentId}`,
        updateData
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Unknown error occurred",
      };
    }
  },

  // Start a progress step
  async startProgressStep(stepId: number): Promise<ApiResponse<ProgressStep>> {
    try {
      const data = await apiClient.patch<{ data: ProgressStep }>(
        `/appointment-progress/${stepId}/start`
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // End/complete a progress step (optional note e.g. for pause reason)
  async endProgressStep(
    stepId: number,
    note?: string
  ): Promise<ApiResponse<ProgressStep>> {
    try {
      const body = {
        isPause: note != null,
        ...(note != null && { notes: note }),
      };
      const data = await apiClient.patch<{ data: ProgressStep }>(
        `/appointment-progress/${stepId}/complete`,
        body
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Delete a progress step (soft delete)
  async deleteProgressStep(stepId: number): Promise<ApiResponse<void>> {
    try {
      await apiClient.delete(`/appointment-progress/${stepId}`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Get professionals assigned to a step
  async getStepProfessionals(stepId: string): Promise<ApiResponse<any[]>> {
    try {
      const data = await apiClient.get<{ data: any[] }>(
        `/appointment-progress/${stepId}/professionals`
      );

      return {
        success: true,
        data: data.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
        data: [],
      };
    }
  },

  // Associate multiple professionals to a step
  async associateProfessionalsToStep(
    stepId: string,
    professionalIds: string[]
  ): Promise<ApiResponse<any>> {
    try {
      const data = await apiClient.post<{ data: any }>(
        `/appointment-progress/${stepId}/professionals`,
        { professionalIds }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Assign professional to step
  async assignProfessionalToStep(
    stepId: string,
    professionalId: string
  ): Promise<ApiResponse<any>> {
    try {
      const data = await apiClient.post<{ data: any }>(
        `/appointment-progress/${stepId}/professionals/add`,
        { professionalId }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Remove professional from step
  async removeProfessionalFromStep(
    stepId: string,
    professionalId: string
  ): Promise<ApiResponse<any>> {
    try {
      const data = await apiClient.delete<{ data: any }>(
        `/appointment-progress/${stepId}/professionals/${professionalId}`
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Add subservices to appointment
  async addSubServicesToAppointment(
    appointmentId: number,
    subServiceIds: number[]
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.post<{ data: Appointment }>(
        `/appointments/${appointmentId}/subservices`,
        { subServiceIds }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Create appointment progress
  async createAppointmentProgress(
    appointmentId: number,
    serviceSubServiceId: number
  ): Promise<ApiResponse<any>> {
    try {
      const data = await apiClient.post<{ data: any }>(
        "/appointment-progress",
        {
          appointmentId,
          serviceSubServiceId,
        }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  },

  // Create a new appointment manually
  async createAppointment(appointmentData: {
    clientId: number;
    professionalId: number;
    serviceId: number;
    appointmentDate: string;
    startTime?: string;
  }): Promise<ApiResponse<Appointment>> {
    try {
      const requestBody: any = {
        clientId: appointmentData.clientId,
        professionalId: appointmentData.professionalId,
        serviceId: appointmentData.serviceId,
        appointmentDate: appointmentData.appointmentDate,
      };

      // Only include startTime if provided
      if (appointmentData.startTime) {
        requestBody.startTime = appointmentData.startTime;
      }

      const data = await apiClient.post<{ data: Appointment }>(
        "/appointments/admin",
        requestBody
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to create appointment",
      };
    }
  },

  // Reschedule an existing appointment
  async rescheduleAppointment(
    appointmentId: number,
    newDate: string,
    newTime?: string
  ): Promise<ApiResponse<Appointment>> {
    try {
      const requestBody: any = {
        appointmentDate: newDate,
      };

      // Only include startTime if provided
      if (newTime) {
        requestBody.startTime = newTime;
      }

      const data = await apiClient.put<{ data: Appointment }>(
        `/appointments/${appointmentId}`,
        requestBody
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to reschedule appointment",
      };
    }
  },

  // Cancel an appointment
  async cancelAppointment(
    appointmentId: number
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.patch<{ data: Appointment }>(
        `/appointments/${appointmentId}/cancel`
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to cancel appointment",
      };
    }
  },

  // Reactivate a cancelled appointment
  async reactivateAppointment(
    appointmentId: number
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.put<{ data: Appointment }>(
        `/appointments/${appointmentId}/`,
        { status: "scheduled" }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to reactivate appointment",
      };
    }
  },

  // Update appointment payment
  async updatePayment(
    appointmentId: number,
    paymentMethod: PaymentMethod
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.patch<{ data: Appointment }>(
        `/appointments/${appointmentId}/payment`,
        { payment: paymentMethod }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update payment",
      };
    }
  },

  async getWeekAppointments(
    date?: string
  ): Promise<ApiResponse<Appointment[]> & { meta?: WeekAppointmentsMeta }> {
    try {
      const query = date ? `?date=${encodeURIComponent(date)}` : "";
      const response = await apiClient.get<{
        data: Appointment[];
        meta: WeekAppointmentsMeta;
      }>(`/appointments/week${query}`);

      return {
        success: true,
        data: response.data || [],
        meta: response.meta,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load week appointments",
      };
    }
  },

  async getMonthAppointments(
    year: number,
    month: number
  ): Promise<
    ApiResponse<Appointment[]> & {
      meta?: {
        year: number;
        month: number;
        startDate: string;
        endDate: string;
        count: number;
      };
    }
  > {
    try {
      const query = `?year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`;
      const response = await apiClient.get<{
        data: Appointment[];
        meta: {
          year: number;
          month: number;
          startDate: string;
          endDate: string;
          count: number;
        };
      }>(`/appointments/month${query}`);

      return {
        success: true,
        data: response.data || [],
        meta: response.meta,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load month appointments",
      };
    }
  },

  async markConfirmationSent(
    appointmentId: number
  ): Promise<ApiResponse<Appointment>> {
    try {
      const data = await apiClient.patch<{ data: Appointment }>(
        `/appointments/${appointmentId}/confirmation-sent`,
        { confirmationSent: true }
      );

      return {
        success: true,
        data: data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update confirmation status",
      };
    }
  },
};
